import React from 'react';
import { Check } from 'lucide-react';

const APP_STORE_URL = 'https://apps.apple.com/app/id6751061434';

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Get started — no commitment required.',
    features: [
      'Up to 2 active commitments',
      'Up to 10 total commitments',
      'Countdown timers',
      'Basic streak tracking',
      'Basic analytics'
    ],
    cta: 'Download Free',
    highlight: false
  },
  {
    name: 'Elite',
    price: '$34.99',
    period: '/year',
    description: 'Best value — 7-day free trial included.',
    badge: 'Most Popular',
    discount: 'Save 58% vs monthly',
    features: [
      'Unlimited commitments',
      'Up to 5 active commitments',
      'Timer customization',
      'Full analytics & insights',
      'Streak tracking',
      'Custom notifications',
      'Custom themes',
      'Beta features'
    ],
    cta: 'Start Free Trial',
    highlight: true
  },
  {
    name: 'Monthly',
    price: '$6.99',
    period: '/month',
    description: 'Flexible — cancel anytime.',
    features: [
      'Unlimited commitments',
      'Up to 5 active commitments',
      'Timer customization',
      'Full analytics & insights',
      'Streak tracking',
      'Custom notifications',
      'Custom themes',
      'Beta features'
    ],
    cta: 'Go Premium',
    highlight: false
  }
];

const Pricing = () => {
  return (
    <section id="pricing" className="relative py-32 px-6 lg:px-12" style={{ background: '#050508' }}>
      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-20">
          <div className="section-label">Pricing</div>
          <h2 className="text-4xl lg:text-6xl font-black tracking-tight mb-5">
            Start free.{' '}
            <span className="gradient-text">Upgrade when ready.</span>
          </h2>
          <p className="text-lg text-[rgba(255,255,255,0.4)] max-w-xl mx-auto">
            No tricks. Free plan is genuinely useful. Premium unlocks unlimited everything.
          </p>
        </div>

        {/* Plan cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((plan, index) => (
            <div key={index} className="relative">
              {/* Popular badge */}
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-1.5 rounded-full text-xs font-mono font-bold text-white whitespace-nowrap z-20" style={{ background: 'linear-gradient(135deg, #7C3AED, #00D26A)' }}>
                  {plan.badge}
                </div>
              )}

              <div
                className="cyber-card p-8 h-full flex flex-col"
                style={plan.highlight ? { borderColor: 'rgba(124, 58, 237, 0.4)' } : {}}
              >
                {/* Header */}
                <div className="mb-8">
                  <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
                  <p className="text-sm text-[#6b7280] mb-5">{plan.description}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-5xl font-black text-white">{plan.price}</span>
                    <span className="text-[#6b7280] text-sm">{plan.period}</span>
                  </div>
                  {plan.discount && (
                    <div className="mt-3 text-xs font-mono gradient-text">{plan.discount}</div>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#7C3AED' }} />
                      <span className="text-sm text-[rgba(255,255,255,0.6)]">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full py-3.5 rounded-xl text-sm font-bold text-center transition-all block ${
                    plan.highlight
                      ? 'text-white hover:opacity-90'
                      : 'border border-[rgba(255,255,255,0.12)] text-white hover:border-[rgba(124,58,237,0.4)]'
                  }`}
                  style={plan.highlight ? { background: 'linear-gradient(135deg, #7C3AED, #00D26A)' } : {}}
                >
                  {plan.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Lifetime offer */}
        <div className="mt-10 max-w-5xl mx-auto">
          <div className="cyber-card p-8 flex flex-col md:flex-row items-center justify-between gap-6" style={{ borderColor: 'rgba(124, 58, 237, 0.2)' }}>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest gradient-text mb-2">Limited offer</div>
              <h3 className="text-2xl font-black text-white mb-1">Lifetime Access — $79.99</h3>
              <p className="text-sm text-[rgba(255,255,255,0.45)]">Pay once, own it forever. All premium features, all future updates.</p>
            </div>
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 px-8 py-3.5 rounded-xl text-sm font-bold border border-[rgba(124,58,237,0.3)] gradient-text hover:bg-[rgba(124,58,237,0.08)] transition-all whitespace-nowrap"
            >
              Get Lifetime Access
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
